import * as React from 'react'

import {
  Avatar,
  Box,
  BoxList,
  Button,
  CounterBadge,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { CreateApprovalsApprovalFlowStepsInput as StepArgs } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'

import BulkApproversUpdateModal from './BulkApproversUpdateModal'
import { StepApprover } from './StepApprover'

interface Props {
  step: StepArgs
  index: number
  canEdit: boolean
  canDeleteSteps: boolean
  onAddApprover: (step: StepArgs) => void
  onUpdateStep: (step: StepArgs) => void
  removeApproverFromStep: (stepPosition: number, approverIndex: number) => void
  onDeleteStep: (step: StepArgs) => void
  contractVersions: ContractVersions
}

export const ApprovalStep = ({
  step,
  index,
  canEdit,
  canDeleteSteps,
  onAddApprover,
  onUpdateStep,
  onDeleteStep,
  contractVersions,
}: Props) => {
  const [showEditModal, setShowEditModal] = React.useState(false)

  const moreThanOneApprover = step.approvers.length > 1
  const alignItems = moreThanOneApprover ? 'flexStart' : 'center'

  return (
    <>
      <BoxList.Item>
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          width="full"
          alignItems="flexStart"
          gap="s24"
        >
          <Box flexDirection="row" gap="s12" alignItems={alignItems} width="full">
            <CounterBadge count={index + 1} noCountIcon="filter" />
            <Box
              flexDirection="column"
              justifyContent="spaceBetween"
              alignItems="flexStart"
              gap="s12"
            >
              <Box flexDirection="row" gap="s6" alignItems="center" flexWrap="wrap">
                {step.approvers.map((approver, approverIndex) => (
                  <StepApprover key={approverIndex} approver={approver} />
                ))}
                {canEdit && (
                  <Touchable onClick={() => onAddApprover(step)}>
                    <Avatar size="s32" label="+" dotted />
                  </Touchable>
                )}
              </Box>
              {moreThanOneApprover && (
                <Text size="200">
                  {i18n.t('settings.approvals.approval_level.strategy.anyofall')}
                </Text>
              )}
            </Box>
          </Box>
          {canEdit && (
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.TouchableOption
                  title={i18n.t('settings.approvals.approval_flow.modify')}
                  onClick={() => setShowEditModal(true)}
                />
                {canDeleteSteps && (
                  <PopoverMenu.TouchableOption
                    title={i18n.t('settings.approvals.approval_flow.delete')}
                    onClick={() => onDeleteStep(step)}
                    danger
                  />
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
          )}
        </Box>
      </BoxList.Item>
      {showEditModal && (
        <BulkApproversUpdateModal
          onSubmit={(stepUpdated) => onUpdateStep(stepUpdated)}
          step={step}
          onClose={() => setShowEditModal(false)}
          contractVersions={contractVersions}
        />
      )}
    </>
  )
}
